import axios from 'axios';
import { auth } from '../firebase/config';
import { clearAuthData, SESSION_EXPIRED_EVENT } from '../utils/auth';
import env from '../config/environments';
import { IS_LOCAL_HARNESS, localAuthToken } from '../config/localSession';
import { ADMIN_VIEW_HEADER, adminViewTokenAsync, adminViewTrialId, isAdminView } from '../config/adminViewSession';

const CANDIDATE_API_URL = env.candidateApiUrl;
export const CORE_API_URL = env.coreApiUrl;

/**
 * Candidate-portal backend instance.
 * All requests include credentials so the session cookie is sent automatically.
 */
const candidateApi = axios.create({
    baseURL: CANDIDATE_API_URL,
    withCredentials: true,
});

/**
 * Core-API instance — handles authentication endpoints
 * (login, signup, logout, firebase-token).
 */
export const coreApi = axios.create({
    baseURL: CORE_API_URL,
    withCredentials: true,
});

/**
 * Admin view: the admin's token on every read, and no writes at all.
 *
 * An admin-view request must never pick up a candidate credential: this origin may well hold
 * one, from whoever last signed in on this browser, and sending it would authenticate the wrong
 * person entirely. Both session interceptors below therefore bail out on `isAdminView()` of
 * their own accord, rather than this one relying on running first — axios runs the
 * last-registered request interceptor first, which is not a fact a credential choice should
 * quietly depend on.
 *
 * **The write block is a client-side echo of a server-side rule, not the rule itself.** The
 * candidate API refuses every non-safe method in this mode before any handler runs, and that is
 * what makes read-only real. This exists because the workspace fires writes nobody asked it to —
 * a presence beat every 45 s, an activity log every 5 minutes, read receipts when a memo scrolls
 * past, view telemetry on a document opening — and every one of those would otherwise leave the
 * admin's browser, be refused, and log a console error. Blocking them here keeps the network
 * quiet and the console clean; it is not what keeps the candidate's record clean.
 *
 * The rejection is shaped like an axios error with `skipAuthRedirect` so the workspace's existing
 * error handling treats it as a failed request rather than an expired session.
 */
candidateApi.interceptors.request.use(async (config) => {
    if (!isAdminView()) return config;

    const method = (config.method || 'get').toLowerCase();
    if (method !== 'get' && method !== 'head') {
        const blocked = new Error('This workspace is open in admin view, which cannot change anything.');
        blocked.isAdminViewReadOnly = true;
        blocked.config = { ...config, skipAuthRedirect: true };
        return Promise.reject(blocked);
    }

    const token = await adminViewTokenAsync();
    if (token) config.headers['auth-token'] = token;
    config.headers[ADMIN_VIEW_HEADER] = adminViewTrialId();
    // A 401 here must not bounce the frame to /login: the admin has no candidate session to
    // restore, and a hard navigation inside the iframe would replace the workspace with a login
    // form in the admin portal's panel.
    config.skipAuthRedirect = true;
    return config;
});

/**
 * Whether the candidate API is a process on this machine.
 *
 * True only when `REACT_APP_CANDIDATE_API_URL` moved it there (see
 * `config/environments.js`) or under the local harness bundle. A deployed build points
 * at a real host and this is false, so everything guarded by it is dropped.
 */
const CANDIDATE_API_IS_LOCAL =
    /^https?:\/\/(127\.0\.0\.1|localhost|\[::1\])(:|\/|$)/i.test(CANDIDATE_API_URL || '');

/**
 * A backend on this machine cannot be reached with the session cookie.
 *
 * The cookie belongs to `.applisolutions.com` — the browser will not send it to
 * 127.0.0.1 whatever `withCredentials` says — so a candidate signed in perfectly well
 * would reach a local backend as an anonymous caller and be bounced to /login by the
 * 401 interceptor below. Both branches attach the `auth-token` header instead, which is
 * the fallback appli-common's `enforce_permission` documents for exactly this: "for
 * environments where cookies are unavailable (e.g. local development)".
 *
 * The two differ only in what the token *is*. Under the harness there is no Firebase
 * project to sign into, so it is the stub the local backends' patched verifier accepts.
 * Against real dev it is the session's own Firebase ID token, verified by the backend
 * exactly as a deployed one would be — nothing here weakens what the server checks.
 * Both are dead code in a deployed build.
 */
if (IS_LOCAL_HARNESS) {
    candidateApi.interceptors.request.use((config) => {
        // Never in admin view: that mode carries the admin's own token and must not fall back
        // to whatever session this origin happens to hold. Checked here rather than left to
        // axios's interceptor ordering, which runs the last-registered one first and is not a
        // thing a credential choice should depend on.
        if (isAdminView()) return config;
        const token = localAuthToken();
        if (token) config.headers['auth-token'] = token;
        return config;
    });
} else if (CANDIDATE_API_IS_LOCAL) {
    candidateApi.interceptors.request.use(async (config) => {
        /* Never in admin view — see the note on the harness branch above. */
        if (isAdminView()) return config;
        /* Wait for the SDK to restore the session before reading it. On a fresh load
           `currentUser` is null for the first moment while Firebase rehydrates from
           IndexedDB, and the workspace fires its first reads inside that window — so
           reading it straight would send those without a token, take a 401, and let the
           interceptor below bounce a perfectly signed-in candidate to /login. */
        await auth?.authStateReady?.().catch(() => {});
        // Through the SDK rather than a cached string: it refreshes an hour-old token,
        // and a background poll outliving one is exactly what would break.
        const token = await auth?.currentUser?.getIdToken?.().catch(() => null);
        if (token) config.headers['auth-token'] = token;
        return config;
    });
}

/* One refresh at a time: when a token lapses, every read the page has in flight comes back
   401 together, and they should all wait on the same refresh. */
let refreshing = null;

/** A fresh ID token for whoever the SDK holds, or `''` when there is nobody or it fails. */
const refreshIdToken = () => {
    const user = auth?.currentUser;
    if (!user?.getIdToken) return Promise.resolve('');
    if (!refreshing) {
        refreshing = user.getIdToken(true)
            .then((token) => token || '')
            .catch(() => '')
            .finally(() => { refreshing = null; });
    }
    return refreshing;
};

/**
 * 401 response interceptor.
 * Only attached to the candidate-portal instance (not coreApi) so
 * login/signup 401s don't trigger a redirect loop.
 *
 * ID tokens last an hour, so the first answer to a 401 is a forced refresh and one retry
 * of the same request; the student never sees it. When that is not possible, or the
 * retry is refused too, the session is cleared and `SESSION_EXPIRED_EVENT` fired.
 * Requests with `skipAuthRedirect: true` (the workspace's `KEEP_DRAFT`, interview-session
 * requests) are not redirected — a hard redirect would throw away what was typed — and
 * get the original error back marked `signedOut`, so the screen can say so in place.
 * Admin view is left alone: it carries the admin's token and has no candidate session.
 */
candidateApi.interceptors.response.use(
    (response) => response,
    async (error) => {
        if (error.response?.status !== 401 || isAdminView()) return Promise.reject(error);

        const config = error.config;
        if (config && !config.authRetried) {
            const token = await refreshIdToken();
            if (token) {
                const retry = { ...config, authRetried: true };
                if (retry.headers?.['auth-token']) retry.headers['auth-token'] = token;
                return candidateApi.request(retry);
            }
        }

        const data = error.response.data;
        const errorMessage = typeof data === 'string' ? data : data?.error;
        clearAuthData();
        window.dispatchEvent(new Event(SESSION_EXPIRED_EVENT));

        if (config?.skipAuthRedirect) {
            error.signedOut = true;
            return Promise.reject(error);
        }
        console.warn(`Authentication failed: ${errorMessage} - redirecting to login`);
        window.location.href = '/login';
        return Promise.reject(new Error(errorMessage || 'Session expired. Please log in again.'));
    }
);

export default candidateApi;
